import { isSelfServePlanTier } from "@/shared/pricing-plans";

export function buildOnboardingCallbackURL({
  origin,
  intentId,
  promoCode,
  plan,
  monthlyCredits,
  billingCycle,
  currency,
}: {
  origin: string;
  intentId?: string | null;
  promoCode?: string | null;
  /** personal | team from the pricing page (plan 017); anything else is dropped and means Team */
  plan?: string | null;
  monthlyCredits?: string | null;
  billingCycle?: string | null;
  currency?: string | null;
}) {
  const url = new URL("/onboarding/name", origin);

  if (intentId) {
    url.searchParams.set("intent_id", intentId);
  }
  if (promoCode) {
    url.searchParams.set("promo", promoCode);
  }
  appendPlanSelectionParams(url.searchParams, {
    plan,
    monthlyCredits,
    billingCycle,
    currency,
  });

  return url.toString();
}

function appendPlanSelectionParams(
  target: URLSearchParams,
  values: {
    plan?: string | null;
    monthlyCredits?: string | null;
    billingCycle?: string | null;
    currency?: string | null;
  },
) {
  if (isSelfServePlanTier(values.plan)) {
    target.set("plan", values.plan);
  }
  if (values.monthlyCredits && /^\d{1,7}$/.test(values.monthlyCredits)) {
    target.set("monthly_credits", values.monthlyCredits);
  }
  if (values.billingCycle === "monthly") {
    target.set("billing_cycle", values.billingCycle);
  }
  if (values.currency === "USD" || values.currency === "INR") {
    target.set("currency", values.currency);
  }
}

export function getOnboardingPathFromSearchParams(searchParams: URLSearchParams) {
  const params = new URLSearchParams();
  const intentId = searchParams.get("intent_id");
  const promoCode = searchParams.get("promo");

  if (intentId) params.set("intent_id", intentId);
  if (promoCode) params.set("promo", promoCode);
  appendPlanSelectionParams(params, {
    plan: searchParams.get("plan"),
    monthlyCredits: searchParams.get("monthly_credits"),
    billingCycle: searchParams.get("billing_cycle"),
    currency: searchParams.get("currency"),
  });

  return `/onboarding/name${params.size > 0 ? `?${params.toString()}` : ""}`;
}

export function getPlanSelectionPath(searchParams: URLSearchParams) {
  const params = new URLSearchParams();
  const promoCode = searchParams.get("promo");
  if (promoCode) params.set("promo", promoCode);
  appendPlanSelectionParams(params, {
    plan: searchParams.get("plan"),
    monthlyCredits: searchParams.get("monthly_credits"),
    billingCycle: searchParams.get("billing_cycle"),
    currency: searchParams.get("currency"),
  });

  return `/onboarding/plan${params.size > 0 ? `?${params.toString()}` : ""}`;
}

export function getPostVerificationPath({
  searchParams,
  origin,
}: {
  searchParams: URLSearchParams;
  origin: string;
}) {
  const callbackURL = searchParams.get("callbackURL");

  if (callbackURL) {
    try {
      const parsed = new URL(callbackURL, origin);
      if (parsed.origin === origin) {
        return `${parsed.pathname}${parsed.search}${parsed.hash}`;
      }
    } catch {
      // Fall through to rebuilding the onboarding URL from explicit params.
    }
  }

  return getOnboardingPathFromSearchParams(searchParams);
}

export function getPreservedSearchRedirectTarget({
  to,
  search,
}: {
  to: string;
  search: string;
}) {
  return `${to}${search}`;
}

export function getVerificationResendPayload({
  searchParams,
  origin,
}: {
  searchParams: URLSearchParams;
  origin: string;
}) {
  const token = searchParams.get("token");

  if (!token) {
    return null;
  }

  return {
    token,
    callbackURL: getPostVerificationPath({ searchParams, origin }),
  };
}
